import { FileAction } from 'constants/FileAction';
import { SortDirection } from 'constants/SortDirection';
import { runInAction } from 'mobx';
import log from 'services/helpers/log';
import { buildOptions, buildWhereFilter, get, getCount, getPage, post } from 'stores/_services/dataService';
import { buildCsvDownloadUrl } from 'stores/_services/downloadService';
import { getConditions, getDefaultFilter, getListConditions } from 'stores/_services/filterService';

import BaseListStore, { BaseClassConstructor, DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';
import getAnalysisContext from '../helpers/getAnalysisContext';
import { IDataFetchOptions } from '../types';

const SEARCH_FIELDS = ['path', 'fileRepId'];

interface IFileAnalysisStoreProps<T> {
    itemStore: BaseClassConstructor<T>;
    baseRoute: string;
}

export default class FileAnalysisStore<T> extends BaseListStore<T> {
    private readonly baseRoute: string;

    public get files(): void {
        throw new Error('Files getter unimplemented');
    }

    constructor({ itemStore, baseRoute }: IFileAnalysisStoreProps<T>) {
        super({
            sortBy: 'threatWeight',
            sortDirection: SortDirection.DESC,
            defaultFilter: getDefaultFilter({ file: true }),
            itemStore
        });

        this.baseRoute = baseRoute;
    }

    private async _flagFile(fileRepId: string, flagId: string | number, fileId?: string): Promise<void> {
        await post(`filereplocals/${fileRepId}/flag`, { flagId: flagId === -1 ? undefined : flagId });
        fileId === fileRepId || (fileRepId && !fileId) ? await this.getFileDetail(fileRepId) : await this.getFile(fileId as string);
    }

    private async _patchByQuery(action: FileAction, params: Record<string, unknown> = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT) {
        const conditions = getListConditions(this.selectedIdList, this.filter);

        const { scanId, boxId, route } = await getAnalysisContext(this.baseRoute);
        conditions.push({ scanId, boxId });

        const options = {
            queryParams: {
                where: buildWhereFilter({
                    where: conditions,
                    searchFields: SEARCH_FIELDS
                }),
                ...params
            }
        };
        const { data } = await post(`${route}/${action}`, null, options);

        this.deselectAll();
        await this.getFiles({ reset: true }, pageNumber, pageSize);

        if (action === FileAction.ANALYZE && data?.filerepIds.length) {
            this._updateData(data.filerepIds);
        }
    }

    public async getFileDetail(fileRepId: string): Promise<void> {
        const res = await get(`fileRepLocals/${fileRepId}`);

        const { data } = res;

        if (!data.fileRepId) {
            data.fileRepId = data.sha1;
        }

        this._updateData(data);
    }

    public async getFileRepData(fileRepId: string): Promise<void> {
        const res = await get(`fileRepLocals/${fileRepId}`);
        const { data } = res;
        return data;
    }

    public async getFile(fileId: string, routeOption?: string): Promise<void> {
        let res;

        try {
            if (!routeOption) {
                var { boxId, route } = await getAnalysisContext(this.baseRoute);
            } else {
                route = routeOption;
            }

            if (boxId) {
                const options = {
                    filter: {
                        where: [{ id: fileId }, { boxId }]
                    }
                };

                res = await get(route, options);
            } else {
                res = await get(`${this.baseRoute}/${fileId}`);
            }
        } catch (e) {
            log.error(e as Error);
            return;
        }

        const { data } = res;

        if (!data.fileRepId) {
            data.fileRepId = data.sha1;
        }

        this._updateData(data);
    }

    public async getFiles(args: IDataFetchOptions = {}, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        const shouldReset = this._updateListState(args);
        const conditions = getConditions(this.filter);
        const { scanId, boxId, route } = await getAnalysisContext(this.baseRoute);
        conditions.push({ scanId, boxId });

        const options = {
            filter: {
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: this.sort
            }
        };

        const res = await getPage(route, options);
        const totalCount = await getCount(route, { where: conditions, searchFields: SEARCH_FIELDS });

        if (shouldReset) {
            this.clearIds();
        }

        const { data } = res;

        this._updateData(data);
        this._updateIds(data, 0);
        //every step that updates observables in an asynchronous process should be marked as action(one way to use is runInAction), Only synchronous changes to observable data works in actions
        runInAction(() => {
            this.count = data.length;
            this.totalCount = totalCount;
            this.page = pageNumber;
            this.pageSize = pageSize;
        });
    }

    public async analyzeFile(fileId: string): Promise<void> {
        await post(`filereplocals/${fileId}/analyze`);

        this._updateData({
            id: fileId,
            analyzing: true
        });
    }

    public async flagFile(fileRepId: string, flagId: string | number, fileId?: string): Promise<void> {
        await this._flagFile(fileRepId, flagId, fileId);
    }

    public async analyzeAll(pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        await this._patchByQuery(FileAction.ANALYZE, {}, pageNumber, pageSize);
    }

    public async flagAll(flagId: string | number = -1, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        await this._patchByQuery(FileAction.FLAG, { flagId: flagId === -1 ? null : flagId }, pageNumber, pageSize);
    }

    public async exportToCsv(): Promise<void> {
        const { scanId, boxId, route } = await getAnalysisContext(this.baseRoute);
        const conditions = getListConditions(this.selectedIdList, this.filter);
        conditions.push({ scanId, boxId });

        const options = {
            filter: {
                searchFields: SEARCH_FIELDS,
                where: conditions,
                order: this.sort
            },
            excludeFields: ['fileRepId', 'id', 'flagId', 'boxId']
        };

        const query = buildOptions(options);

        window.location.href = await buildCsvDownloadUrl(route, query);
    }

    public async retrieveFile(fileId: string): Promise<void> {
        await post(`filereplocals/${fileId}/retrieve`);
    }
}
